import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

const STORAGE_KEY = "vavr.web.sidebar-width";
const DEFAULT_WIDTH = 248;
const MIN_WIDTH = 200;
const MAX_WIDTH = 360;
const clamp = (width: number, max = MAX_WIDTH) => Math.round(Math.min(max, Math.max(MIN_WIDTH, width)));
const availableWidth = () => Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - 520));

function savedWidth() {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? clamp(saved) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function rememberWidth(width: number) {
  try { localStorage.setItem(STORAGE_KEY, String(width)); } catch { /* Resizing still works when storage is unavailable. */ }
}

/** Web-only controls around the shared console; mobile and kiosk shells never mount this. */
export function WebConsoleFrame({ children }: { children: ReactNode }) {
  const [preferredWidth, setPreferredWidth] = useState(savedWidth);
  const [maxWidth, setMaxWidth] = useState(availableWidth);
  const [resizing, setResizing] = useState(false);
  const drag = useRef<{ x: number; start: number; width: number; pointerId: number } | null>(null);
  const width = clamp(preferredWidth, maxWidth);

  useEffect(() => {
    const onResize = () => setMaxWidth(availableWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function finishResize() {
    if (!drag.current) return;
    rememberWidth(drag.current.width);
    drag.current = null;
    setResizing(false);
  }

  function chooseWidth(next: number) {
    const value = clamp(next, maxWidth);
    setPreferredWidth(value);
    rememberWidth(value);
  }

  return (
    <div className={`web-console${resizing ? " web-console--resizing" : ""}`} style={{ "--web-sidebar-width": `${width}px` } as CSSProperties}>
      {children}
      <aside className="web-sidebar-controls" aria-label="Sidebar controls">
        <div
          className="web-sidebar-resize"
          role="separator"
          aria-label="Sidebar width"
          aria-orientation="vertical"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={maxWidth}
          aria-valuenow={width}
          aria-valuetext={`${width} pixels`}
          tabIndex={0}
          title="Drag to resize · Arrow keys to adjust · Double-click or Enter to reset"
          onPointerDown={(event) => {
            if (event.button !== 0 || !event.isPrimary) return;
            event.preventDefault();
            event.currentTarget.focus();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, start: width, width, pointerId: event.pointerId };
            setResizing(true);
          }}
          onPointerMove={(event) => {
            const current = drag.current;
            if (!current || current.pointerId !== event.pointerId) return;
            current.width = clamp(current.start + event.clientX - current.x, maxWidth);
            setPreferredWidth(current.width);
          }}
          onPointerUp={finishResize}
          onPointerCancel={finishResize}
          onLostPointerCapture={finishResize}
          onDoubleClick={() => chooseWidth(DEFAULT_WIDTH)}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 32 : 8;
            const next = event.key === "ArrowLeft" ? width - step
              : event.key === "ArrowRight" ? width + step
              : event.key === "Home" ? MIN_WIDTH
              : event.key === "End" ? maxWidth
              : event.key === "Enter" ? DEFAULT_WIDTH : undefined;
            if (next === undefined || drag.current) return;
            event.preventDefault();
            chooseWidth(next);
          }}
        ><span aria-hidden="true" /></div>
      </aside>
    </div>
  );
}
